import { apiFetch, ApiError } from './client';
import type { ObjectSnapshot, Persona, ViewDiff } from './uiSignoff';

export { errorCode, errorMessage } from './uiSignoff';

// PR review: staff only. What a client PR changes on record pages, and the business review recorded
// against its head commit. The API decides who sees and who records; this file only calls it.

export interface Info {
  configured: boolean;
  repo: string | null;
  objects: string[];
  label: string;
  statement: { text: string; version: number };
  canReadRepos: boolean;
  /** Plain-English explanations are on (PR_REVIEW_MODEL and an API key set). Off: facts only. */
  narration?: boolean;
  reviewer: { roleLabel: string } | null;
}

export interface Narrative { summary: string; groups: { groupId: string; title: string; explanation: string; sources: string[] }[] }
export type NarrationState =
  | { state: 'off' }
  | { state: 'ready'; model: string; narrative: Narrative; createdAt: string }
  | { state: 'failed'; model: string; error: string; retryAfter: string | null };

export interface Toggle {
  on: boolean;
  counted: boolean;
  by: string | null;
  at: string | null;
  reason: 'not_labeled' | 'no_event' | 'not_eligible' | null;
}

export interface Pr {
  number: number;
  title: string;
  state: 'open' | 'merged' | 'closed';
  draft: boolean;
  url: string;
  author: string | null;
  headSha: string;
  headRef: string;
  baseRef: string;
  fork: boolean;
  createdAt: string;
  updatedAt: string;
  mergedAt: string | null;
  toggle: Toggle;
}

export type ReviewSummaryState = 'approved' | 'changes_requested' | 'stale' | 'none';
export interface PrRow extends Pr {
  review: { state: ReviewSummaryState; by: string | null; at: string | null };
  /** What was found at the PR's current head, once someone has opened it; null until then. */
  analysis: AnalysisSummary | null;
}
export interface IgnoredPr { number: number; title: string; url: string; state: Pr['state']; toggle: Toggle }
export interface Listing { repo: string; label: string; prs: PrRow[]; ignored: IgnoredPr[] }

export interface ChangedFile { path: string; status: string; additions: number; deletions: number; previousPath: string | null }
export interface PageChange { object: string; views: { key: string; status: 'added' | 'removed' | 'changed' }[]; missing: 'base' | 'head' | null }
export type Timing = 'before_save' | 'after_save' | 'before_delete' | 'after_delete' | 'scheduled';
export type AutomationKind = 'flow' | 'trigger' | 'validation_rule' | 'workflow_rule' | 'duplicate_rule' | 'assignment_rule';
export interface AutomationRef { kind: AutomationKind; name: string; object: string | null; source: string; path: string; repo: string; sha: string }
export interface Write { object: string; how: 'record' | 'create' | 'update' | 'delete'; fields: string[] }
export type FlagKind = 'removes_behavior' | 'same_field_same_moment' | 'unordered_siblings' | 'rule_blocks_write';
export interface Flag { id: string; kind: FlagKind; object: string; timing: Timing | null; fields: string[]; subject: AutomationRef; other: AutomationRef | null; detail: string; notice: string }
export interface InventoryItem extends AutomationRef {
  label: string | null; timings: Timing[]; events: string[]; active: boolean; reads: string[]; writes: Write[] | null; opaque: string | null; triggerOrder: number | null; changed: boolean;
}
export interface Overlap {
  flags: Flag[];
  unknown: { subject: AutomationRef; object: string; timing: Timing; others: (AutomationRef & { opaque: string | null })[] }[];
  inventory: { object: string; automations: InventoryItem[] }[];
  packages: { dependency: string; package: string; version: string; repo: string; tag: string; root: string }[];
  problems: string[];
  changed: string[];
}

export type Badge = 'pages' | 'automation' | 'access' | 'data model' | 'code';
export interface Fact {
  key: string; kind: string; name: string; label: string | null; object: string | null; status: 'added' | 'removed' | 'changed';
  files: string[]; rows: { label: string; value: string }[]; refs: string[]; test: boolean; note: string | null;
}
export interface Group { id: string; title: string; members: string[]; files: string[]; kinds: string[] }
export type Level = 'none' | 'read' | 'edit';
export interface AccessGrid {
  personas: { id: string; label: string }[];
  rows: { object: string; field: string; cells: Record<string, { before: Level; after: Level }> }[];
  note: string | null;
}
export interface Explained {
  facts: Fact[]; groups: Group[]; unexplained: string[]; anchors: Record<string, string>; access: AccessGrid | null; badges: Badge[];
}
export interface AnalysisSummary { prNumber: number; headSha: string; badges: Badge[]; unexplained: number; flags: number; unknown: number }

export interface Analysis {
  id: string;
  baseSha: string;
  headSha: string;
  pages: PageChange[];
  files: ChangedFile[];
  filesTruncated: boolean;
  overlap: Overlap | null;
  overlapError: string | null;
  facts: Explained | null;
  factsError: string | null;
  createdAt: string;
}

export interface Review {
  id: string;
  headSha: string;
  kind: 'signed' | 'revoked';
  revokesId: string | null;
  outcome: 'approved' | 'changes_requested' | null;
  note: string | null;
  statementText: string | null;
  statementVersion: number | null;
  accepted: { analysisId: string; baseSha: string; changedViews: { object: string; key: string; status: string }[]; fileCount: number; flags?: { id: string; kind: FlagKind; object: string; detail: string }[] } | null;
  reviewerName: string | null;
  reviewerEmail: string;
  reviewerRole: string;
  createdAt: string;
  status: 'current' | 'stale' | 'revoked' | null;
  revokedAt: string | null;
}

export interface PrDetail {
  repo: string;
  pr: Pr;
  analysis: Analysis | null;
  /** Why the pages are not drawn, e.g. a fork. */
  notDrawn: { error: string; message: string } | null;
  reviews: Review[];
  current: string | null;
  mine: string | null;
  reviewer: { roleLabel: string } | null;
}

export interface PagesResponse {
  base: { ref: string; commit: string; object: ObjectSnapshot };
  head: { ref: string; commit: string; object: ObjectSnapshot };
  views: ViewDiff[];
  personas: Persona[];
}

const base = (slug: string) => `/clients/${encodeURIComponent(slug)}/modules/pr-review`;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Drawing a PR's two commits can outlast the proxy; the API then answers 202 `{ analyzing: true }`
 * and carries on. Ask again until it's ready.
 */
export async function untilAnalyzed<T>(call: () => Promise<T | { analyzing: true }>, onDrawing?: () => void, pollMs = 3_000, tries = 60): Promise<T> {
  for (let i = 0; i < tries; i++) {
    const r = await call();
    if (!(r as { analyzing?: boolean } | null)?.analyzing) return r as T;
    onDrawing?.();
    await sleep(pollMs);
  }
  throw new ApiError(504, 'Still drawing the pull request after several minutes. Try again in a moment.');
}

export const getInfo = (slug: string, token: string | null) => apiFetch<Info>(`${base(slug)}/`, token);
export const listPrs = (slug: string, token: string | null) => apiFetch<Listing>(`${base(slug)}/prs`, token, { timeoutMs: 60_000 });
export const getPr = (slug: string, n: number, token: string | null, onDrawing?: () => void) =>
  untilAnalyzed(() => apiFetch<PrDetail | { analyzing: true }>(`${base(slug)}/prs/${n}`, token, { timeoutMs: 120_000 }), onDrawing);
export const getPages = (slug: string, n: number, object: string, token: string | null, onDrawing?: () => void) =>
  untilAnalyzed(() => apiFetch<PagesResponse | { analyzing: true }>(`${base(slug)}/prs/${n}/pages?${new URLSearchParams({ object })}`, token, { timeoutMs: 120_000 }), onDrawing);
/** Written once per analysis and kept; the first time can take a minute. */
export async function getNarrative(slug: string, n: number, token: string | null, onDrawing?: () => void): Promise<NarrationState> {
  for (let i = 0; i < 60; i++) {
    const r = await apiFetch<NarrationState | { narrating: true } | { analyzing: true }>(`${base(slug)}/prs/${n}/narrative`, token, { timeoutMs: 120_000 });
    if (!('narrating' in r) && !('analyzing' in r)) return r;
    onDrawing?.();
    await sleep(3_000);
  }
  throw new ApiError(504, 'Still writing the explanation after several minutes. Try again in a moment.');
}
export const recordReview = (slug: string, n: number, body: { outcome: 'approved' | 'changes_requested'; note?: string; headSha: string; acknowledged?: string[] }, token: string | null) =>
  apiFetch<Review>(`${base(slug)}/prs/${n}/reviews`, token, { method: 'POST', body: JSON.stringify(body), timeoutMs: 60_000 });
export const withdrawReview = (slug: string, n: number, id: string, token: string | null) =>
  apiFetch<Review>(`${base(slug)}/prs/${n}/reviews/${encodeURIComponent(id)}/revoke`, token, { method: 'POST', body: '{}' });

// ---------- admin ----------

export interface AdminConfig { clientId: string; reviewLabel: string; statementText: string | null; statementVersion: number; updatedAt: string | null }
export interface AdminReviewer { userId: string; email: string; name: string | null; roleLabel: string }
export interface AdminState { config: AdminConfig; reviewers: AdminReviewer[]; candidates: { userId: string; email: string; name: string | null; staffRole: 'admin' | 'client_admin' }[] }

const admin = (clientId: string) => `/admin/clients/${encodeURIComponent(clientId)}/pr-review`;
export const getAdmin = (clientId: string, token: string | null) => apiFetch<AdminState>(`${admin(clientId)}/`, token);
export const saveAdminConfig = (clientId: string, body: { reviewLabel: string; statementText: string | null }, token: string | null) =>
  apiFetch<AdminConfig>(`${admin(clientId)}/config`, token, { method: 'PUT', body: JSON.stringify(body) });
export const setReviewer = (clientId: string, userId: string, roleLabel: string | null, token: string | null) =>
  apiFetch<{ ok: true }>(`${admin(clientId)}/reviewers/${encodeURIComponent(userId)}`, token, { method: 'PUT', body: JSON.stringify({ roleLabel }) });
